/**
 * Opener · the essay that introduces a chapter, at `?opener=<slug>`.
 *
 * The gap this closes. Until now the product explained every command and
 * never once explained the point. A learner finished two levels of shell
 * basics and was handed `git init` with a one-line justification, which is
 * the steepest step in the course and the one place the map marked with
 * nothing at all. An opener is the missing unit: what this part of the job
 * is, where it came from, why the industry settled on it, and what it costs
 * not to know it.
 *
 * Deliberately not a modal. The warm-up is a modal because it is two minutes
 * of syntax you need in your hands right now; an opener is a thousand words
 * you are meant to sit still for, and anything that opens over a level gets
 * dismissed by reflex. So it is a screen, with a URL, reachable three ways:
 * from the card at the head of a world on the map, from the Next button when
 * a cleared level hands over to a new chapter, and from a link somebody sent
 * you.
 *
 * Nothing here is earned and nothing here is gated. There is no `cleared`
 * check the way the debrief has one, because an opener is the argument for
 * starting rather than the explanation of what you did — the reader who needs
 * it most has not played anything yet. It is a static asset for the same
 * reason (web/src/lib/openers.ts).
 *
 * ## Two languages
 *
 * The essay is fetched in the reader's own, and a chapter nobody has
 * translated yet answers with the English one rather than with an apology
 * (lib/openers.ts). That is why the prose carries its own `dir` and `lang`
 * instead of inheriting the document's: on an Arabic page showing an
 * untranslated chapter, the page is right-to-left and this one essay is not.
 */
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { OpenerProps } from '../contracts';
import type { PublicMission } from '../../types';
import { LOCALE_DIR, type Locale } from '../../lib/locale';
import { useLocale, useStrings } from '../../state/useLocale';
import { fetchOpener, markOpenerSeen, type OpenerResult } from '../../lib/openers';
import { md } from '../../lib/md';
import { useContents, type Heading } from '../../lib/contents';
import { Brand, Button, LinkButton, Tag } from '../../ui';
import { COURSE_HREF } from '../../lib/landing';
import { EXPLAINERS } from '../explainer';

export function Opener({ slug: openerSlug, missions }: OpenerProps) {
  const [result, setResult] = useState<OpenerResult | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { locale } = useLocale();
  const strings = useStrings();
  const s = strings.reading;

  const opener = result?.state === 'ready' ? result.opener : null;

  useEffect(() => {
    let live = true;
    setResult(null);
    void fetchOpener(openerSlug, locale).then((r) => {
      if (live) setResult(r);
    });
    return () => {
      live = false;
    };
  }, [openerSlug, locale]);

  /**
   * Offered. Written the moment the essay is on screen rather than when the
   * reader reaches the end: the record means "this learner has been shown the
   * chapter introduction", not "this learner read it". Marking it only on a
   * finish would put the same page in front of anyone who skimmed and left,
   * every time, which is how a thing to read becomes a thing to dismiss.
   */
  useEffect(() => {
    if (opener) markOpenerSeen(opener.slug);
  }, [opener]);

  // The contents rail, shared with the level debriefs (web/src/lib/contents.ts).
  // Keyed on the opener rather than on its body: the essay arrives inside a
  // fetched object, so the object is what says there is something new to scan.
  const { headings, active } = useContents(body, opener);

  /**
   * The chapter's animated explainer, when it has one (components/explainer).
   * Keyed on the slug in the URL rather than on the fetched essay, so the
   * scenes start downloading alongside the prose instead of after it; drawn
   * only once the essay has arrived, so a chapter that failed to load does not
   * show a picture of an argument it cannot make.
   */
  const Explainer = useMemo(() => {
    const load = EXPLAINERS[openerSlug];
    if (!load) return null;
    const scenes = load(); // started now, not when React first renders it
    return lazy(() => scenes);
  }, [openerSlug]);

  /**
   * The level this chapter opens on, which is where the footer sends the
   * reader. Looked up by level number rather than carried in the URL, so a
   * shared link cannot point the Start button at the wrong level.
   */
  const first = useMemo<PublicMission | null>(
    () => (opener ? (missions.find((m) => m.level === opener.firstLevel) ?? null) : null),
    [missions, opener],
  );

  /**
   * The language the essay came back in, which is not always the one the
   * interface is in. Everything written by the build — the title, the lede
   * and the essay — is set in it; everything around them is the reader's.
   */
  const proseLocale: Locale = opener?.locale ?? locale;
  const proseDir = LOCALE_DIR[proseLocale];

  /**
   * The world's name is translated here rather than in the data: `world:` is
   * the join key between the openers, the missions and the map, so it stays
   * English everywhere it is stored and becomes a name where it is drawn
   * (docs/i18n.md §6.1). The catalogue is keyed by the English string.
   */
  const worldName = opener ? (strings.home.worlds[opener.world]?.name ?? opener.world) : '';

  return (
    <div className="opener">
      <header className="opener-top">
        <Brand compact />
        <nav className="opener-top-nav">
          <LinkButton variant="quiet" href={COURSE_HREF}>
            {s.levelList}
          </LinkButton>
          {first && (
            <LinkButton variant="quiet" href={`?mission=${encodeURIComponent(first.id)}`}>
              {s.opener.skipToLevel(first.level)}
            </LinkButton>
          )}
        </nav>
      </header>

      <article className="opener-page">
        {opener && (
          <div className="opener-head">
            <p className="opener-eyebrow">
              <span className="opener-chapter">{s.opener.beforeLevel(opener.firstLevel)}</span>
              <span className="opener-world">{worldName}</span>
            </p>
            <h1 className="opener-title" dir={proseDir} lang={proseLocale}>
              {opener.title}
            </h1>
            <p className="opener-lede" dir={proseDir} lang={proseLocale}>
              {opener.lede}
            </p>
            <p className="opener-meta">
              <Tag>{s.opener.span(opener.levels, opener.firstLevel, opener.lastLevel)}</Tag>
              {/* The estimate comes from the build rather than from a count
                  taken here, because the build has both halves in front of it:
                  it recomputes the minutes from each language's own word count
                  (scripts/build-openers.mjs). A figure worked out from the
                  English essay would tell an Arabic reader this chapter is
                  longer than the one they are about to read. */}
              <span className="opener-read">{s.readingTime(opener.minutes)}</span>
              {/* Said out loud, because the single most common reason a
                  learner skips a page like this is the suspicion that it is
                  homework with a test at the end. */}
              <span className="opener-optional">{s.opener.optional}</span>
            </p>
          </div>
        )}

        {opener && Explainer && (
          <Suspense fallback={null}>
            {/* The chapter goes to the funnel with the watch (docs/analytics.md,
                question 7): the first level and track name it, and the level
                count lets the report find the rest of it. */}
            <Explainer
              chapter={{ level: opener.firstLevel, track: first?.track ?? 'general', levels: opener.levels }}
            />
          </Suspense>
        )}

        <div className="opener-body">
          <Contents headings={headings} active={active} />

          <div className="opener-main">
            {result === null ? (
              <p className="opener-state" aria-live="polite">
                {s.opener.opening}
              </p>
            ) : result.state === 'ready' ? (
              <motion.div
                ref={body}
                className="prose opener-prose"
                dir={proseDir}
                lang={proseLocale}
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.24, ease: [0.2, 0, 0, 1] }}
                dangerouslySetInnerHTML={md(result.opener.body)}
              />
            ) : (
              <Unavailable kind={result.state} />
            )}
          </div>
        </div>

        {opener && (
          <footer className="opener-foot">
            <p className="opener-foot-note">{s.opener.footNote}</p>
            {first ? (
              <>
                <LinkButton variant="solid" size="lg" href={`?mission=${encodeURIComponent(first.id)}`}>
                  {s.opener.start(first.level, first.title)}
                </LinkButton>
                {/* The other door, and the reason it is here rather than on
                    the map: an essay is exactly the moment somebody wants to
                    type one of the commands they have just read about, and
                    until now the only real terminal in this product was
                    inside a graded level. This one grades nothing. */}
                <LinkButton variant="quiet" href={`?sandbox=${encodeURIComponent(first.id)}`}>
                  {s.opener.sandbox}
                </LinkButton>
              </>
            ) : (
              <LinkButton variant="solid" size="lg" href={COURSE_HREF}>
                {s.levelList}
              </LinkButton>
            )}
          </footer>
        )}
      </article>
    </div>
  );
}

/**
 * The contents. Hidden below the width where it would sit on top of the text
 * rather than beside it (opener.css) — not rendered as a collapsed thing the
 * reader has to open, because on a phone the scroll bar is the contents.
 *
 * The rail changes sides on its own: it is the first column of a grid, and a
 * grid's columns follow the document's direction. Nothing here has to know
 * which way the page is set.
 */
function Contents({ headings, active }: { headings: Heading[]; active: string | null }) {
  const s = useStrings().reading;
  if (headings.length < 2) return null;
  return (
    <nav className="opener-toc" aria-label={s.contents}>
      <p className="label">{s.contents}</p>
      <ol>
        {headings.map((h) => (
          <li key={h.id} className={h.id === active ? 'is-here' : undefined}>
            {/* A real anchor, so it is linkable and Back works. The app's
                click delegate only claims hrefs starting with "?", so this is
                left alone and scrolls the way the browser does. */}
            <a href={`#${h.id}`}>{h.text}</a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * The two ways there is no essay here. Neither is a locked door — an opener
 * is never withheld from anybody — so neither offers a way to buy or sign in.
 */
function Unavailable({ kind }: { kind: 'missing' | 'unavailable' }) {
  const s = useStrings().reading;

  if (kind === 'missing') {
    return (
      <div className="opener-gate">
        <h2>{s.opener.missing.title}</h2>
        <p>{s.opener.missing.body}</p>
        <LinkButton variant="quiet" href={COURSE_HREF}>
          {s.levelList}
        </LinkButton>
      </div>
    );
  }

  return (
    <div className="opener-gate">
      <h2>{s.opener.unavailable.title}</h2>
      <p>{s.opener.unavailable.body}</p>
      <Button variant="solid" onClick={() => window.location.reload()}>
        {s.tryAgain}
      </Button>
    </div>
  );
}

export default Opener;
